import React, { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Skeleton,
  Stack,
  Tooltip
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import StarIcon from '@mui/icons-material/Star';
import { Check } from '@mui/icons-material';
import { verificaMenuAberto, verificaTelaAberta } from '../../../services/authentication/auth';
import useListItemsHook from './useListItemsHook';
import './styles.scss';
import IconesMenu from '../../Shared/Enums/IconesMenu';
import { encodeBase64UrlSafe } from '../../Utils/utils';

const ListMenuItems = ({
  menuOpen,
  handleDrawerToggle,
  expanded,
  setExpanded,
  handleChangeAccordion,
  escopoAcesso,
  setEscopoAcesso,
  departamentoSelecionado,
  setDepartamentoSelecionado,
  setScrIframe
}) => {
  const location = useLocation();
  const listaMenuInferiorRef = useRef(null);
  const {
    disableTooltip,
    handleFavorito,
    isPendingMenuPrincipal,
    nomeDepartamento,
    lstMenusPrincipais,
    loadingFavoritoId,
    menuVisivel,
    ehMenuDaEsteira,
    podeExibirMenuTelaControlada,
    menuEhEsteiraCredito,
    subMenusVisiveis,
    obterItensEsteira,
    navegarParaEtapaEsteira
  } = useListItemsHook({
    departamentoSelecionado,
    setDepartamentoSelecionado,
    setScrIframe,
    setEscopoAcesso,
    handleDrawerToggle
  });

  useEffect(() => {
    if (!menuOpen || isPendingMenuPrincipal) return;

    const menuSelecionado = lstMenusPrincipais.find(menu => {
      const subMenus = subMenusVisiveis(menu);
      const possuiAccordion = (ehMenuDaEsteira(menu) && subMenus.length > 0) || subMenus.length > 1;
      return possuiAccordion && subMenus.some(subMenu => verificaTelaAberta(subMenu.rota));
    });

    if (menuSelecionado) {
      setExpanded(menuSelecionado.id);
    }
  }, [location.pathname, menuOpen, isPendingMenuPrincipal, lstMenusPrincipais]);

  useEffect(() => {
    // realiza a animação de scroll para a posição do submenu selecionado, caso exista, quando o menu estiver aberto e não estiver carregando os menus principais
    if (!menuOpen || isPendingMenuPrincipal || !listaMenuInferiorRef.current) return undefined;

    const timeoutId = setTimeout(() => {
      const itemSelecionado =
        listaMenuInferiorRef.current.querySelector('.itens-submenu.active') ||
        listaMenuInferiorRef.current.querySelector('.accordion-menu.active') ||
        listaMenuInferiorRef.current.querySelector('.btn-toggle.active');

      if (typeof itemSelecionado?.scrollIntoView === 'function') {
        itemSelecionado.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    }, 250);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [menuOpen, isPendingMenuPrincipal, location.pathname]);

  const ItemSubMenu = ({ menu, subMenu, icone = false, tipo = 'normal' }) => (
    <Box className={`itens-submenu ${verificaTelaAberta(subMenu.rota) ? 'active' : ''}`}>
      <Grid container className="itens-submenu-container">
        <Grid
          className="texto-icone flex-box items-meio pad-0"
          item
          xs={10}
          sm={10}
          md={10}
          lg={10}
          xl={10}
        >
          <Link
            to={`/iframe/${encodeBase64UrlSafe(departamentoSelecionado)}/${encodeBase64UrlSafe(subMenu.id)}`}
            className="color-unset text-decoration-unset flex-box pad-15 pad-right-0 w-100"
          >
            {icone ?? ''}
            {`${subMenu.id} - ${subMenu.texto}`}
          </Link>
        </Grid>
        <Grid className="pad-10 flex-box items-meio" item xs={2} sm={2} md={2} lg={2} xl={2}>
          <Box
            className="texto-centro flex-box items-meio"
            onClick={() => handleFavorito(menu.id, subMenu.id, subMenu.favorito)}
          >
            {`${menu.id}_${subMenu.id}` === loadingFavoritoId ? (
              <StarOutlineIcon className="estrela-animando" size={20} />
            ) : (
              <>
                {subMenu.favorito && !disableTooltip && (
                  <Tooltip title="Desmarcar como favorito">
                    <StarIcon className="estrela-favorito" />
                  </Tooltip>
                )}

                {!subMenu.favorito && !disableTooltip && (
                  <Tooltip title="Marcar como favorito">
                    <StarOutlineIcon />
                  </Tooltip>
                )}

                {disableTooltip && subMenu.favorito && <StarIcon className="estrela-favorito" />}
                {disableTooltip && !subMenu.favorito && <StarOutlineIcon />}
              </>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Box className={`box-menu-container ${menuOpen ? 'menu-opened' : 'menu-closed'}`}>
      <List className="box-lista-menu superior">
        <ListItem className="pad-right-left-10 margin-top-15 margin-bottom-30 items-centro">
          {menuOpen && (
            <>
              <ListItemText className="pad-right-left-10 nome-departamento">
                {nomeDepartamento}
              </ListItemText>
              <ListItemIcon>
                <ListItemButton className="btn-toggle" disableRipple onClick={handleDrawerToggle}>
                  <CloseIcon className="icon-position" />
                </ListItemButton>
              </ListItemIcon>
            </>
          )}

          {!menuOpen && (
            <ListItemButton disableRipple onClick={handleDrawerToggle} className="btn-toggle">
              <MenuIcon className="icon-chevron" />
            </ListItemButton>
          )}
        </ListItem>
        <hr className="divider-menu" />
      </List>

      {isPendingMenuPrincipal && (
        <Box className="box-loading">
          <Stack className="pad-5" spacing={1}>
            {[...Array(3)].map((_, index) => (
              <React.Fragment key={index}>
                <Skeleton variant="text" width="100%" height={70} />
                <Skeleton variant="rectangular" width="100%" height={50} />
              </React.Fragment>
            ))}
          </Stack>
        </Box>
      )}

      <List ref={listaMenuInferiorRef} className="box-lista-menu inferior">
        {lstMenusPrincipais
          .filter(menu => menuVisivel(menu))
          .map(menu => {
            const itensEsteira = ehMenuDaEsteira(menu) ? obterItensEsteira(menu) : [];
            const deveManterExpandido = menuEhEsteiraCredito(menu) && menuOpen;

            return (
              <ListItem
                key={menu.id}
                className={`menu-list-itens pad-right-left-10 ${menuOpen ? 'menu-open' : ''} ${isPendingMenuPrincipal ? 'display-none' : ''}`}
                id={`menu-list-${menu.id}`}
                tabIndex={-1}
              >
                {((ehMenuDaEsteira(menu) && subMenusVisiveis(menu).length > 0) ||
                  subMenusVisiveis(menu).length > 1) &&
                  menuOpen && (
                    <Accordion
                      expanded={deveManterExpandido || expanded === menu.id}
                      onChange={deveManterExpandido ? undefined : handleChangeAccordion(menu.id)}
                      className={`accordion-menu ${menuEhEsteiraCredito(menu) ? 'accordion-menu-esteira' : ''} ${verificaMenuAberto(menu.id) ? 'active' : ''}`}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        id={`panel-header-${menu.id}`}
                        className={`accordion-sumary ${menuEhEsteiraCredito(menu) ? 'accordion-sumary-esteira' : ''}`}
                      >
                        <ListItemIcon>
                          <IconesMenu name={menu.icone} />
                        </ListItemIcon>
                        <ListItemText primary={menu.texto} />
                      </AccordionSummary>
                      <AccordionDetails
                        className={`accordion-details ${menuEhEsteiraCredito(menu) ? 'accordion-details-esteira' : ''}`}
                      >
                        {menuEhEsteiraCredito(menu) && itensEsteira.length > 0
                          ? itensEsteira.map(item => (
                              <Box
                                key={item.id ?? item.etapa}
                                className={`itens-submenu item-esteira ${item.ativa ? 'active' : ''} ${item.desabilitada ? 'item-esteira-desabilitado' : ''}`}
                                onClick={() => {
                                  if (!item.desabilitada && !item.ativa && item.etapa) {
                                    navegarParaEtapaEsteira(item.etapa);
                                  }
                                }}
                                onKeyDown={event => {
                                  if (
                                    (event.key === 'Enter' || event.key === ' ') &&
                                    !item.desabilitada &&
                                    !item.ativa &&
                                    item.etapa
                                  ) {
                                    event.preventDefault();
                                    navegarParaEtapaEsteira(item.etapa);
                                  }
                                }}
                                role="button"
                                tabIndex={item.desabilitada || !item.etapa ? -1 : 0}
                              >
                                <Box className="itens-submenu-container item-esteira-container">
                                  <Box className="texto-icone item-esteira-conteudo item-esteira-conteudo--sem-icone">
                                    <Box className="item-esteira-textos">
                                      <span className="item-esteira-titulo">{`${item.id} - ${item.texto}`}</span>
                                      {item.emBreve && (
                                        <span className="item-esteira-badge">Em breve</span>
                                      )}
                                    </Box>
                                  </Box>
                                  {item.validada && !item.emBreve && (
                                    <Check className="item-esteira-check" />
                                  )}
                                </Box>
                              </Box>
                            ))
                          : ehMenuDaEsteira(menu)
                            ? subMenusVisiveis(menu).map((subMenu, index) => (
                                <ItemSubMenu
                                  key={`${menu.id}_${subMenu.id}_${index}`}
                                  menu={menu}
                                  subMenu={subMenu}
                                />
                              ))
                            : subMenusVisiveis(menu).map((subMenu, index) => (
                                <ItemSubMenu
                                  key={`${menu.id}_${subMenu.id}_${index}`}
                                  menu={menu}
                                  subMenu={subMenu}
                                />
                              ))}
                      </AccordionDetails>
                    </Accordion>
                  )}

                {menu.subMenus &&
                  !ehMenuDaEsteira(menu) &&
                  subMenusVisiveis(menu).length === 1 &&
                  menuOpen &&
                  podeExibirMenuTelaControlada(subMenusVisiveis(menu)[0]?.controlado) && (
                    <Box
                      className={`accordion-menu ${menuEhEsteiraCredito(menu) ? 'accordion-menu-esteira' : ''} ${verificaMenuAberto(menu.id) ? 'active' : ''}`}
                    >
                      <Box className="item-menu-solo">
                        <ItemSubMenu
                          tipo="solo"
                          menu={menu}
                          subMenu={subMenusVisiveis(menu)[0]}
                          icone={<IconesMenu name={menu.icone} className="item-menu-solo-icone" />}
                        />
                      </Box>
                    </Box>
                  )}

                {!menuOpen && (
                  <Tooltip title={menu.texto} placement="right">
                    <ListItemButton
                      disableRipple
                      onClick={handleDrawerToggle}
                      className={`btn-toggle itens-menu menu-closed icone-position ${verificaMenuAberto(menu.id) ? 'active' : ''}`}
                    >
                      <IconesMenu name={menu.icone} />
                    </ListItemButton>
                  </Tooltip>
                )}
              </ListItem>
            );
          })}
      </List>
    </Box>
  );
};

export default ListMenuItems;
